import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Save, CheckCircle } from 'lucide-react';

export const GlobalSEOTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [seo, setSeo] = useState({
    targetKeywords: '',
    defaultTitle: '',
    defaultDescription: '',
    canonicalUrl: 'https://www.vizionidigjital.com',
    ogImage: '',
    robotsRule: 'index, follow',
    organizationName: 'Vizioni Digjital',
  });

  useEffect(() => {
    const fetchSEO = async () => {
      try {
        const docRef = doc(db, 'site_settings', 'seo');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSeo({ ...seo, ...docSnap.data() });
        }
      } catch (err) {
        console.error("Failed to load SEO settings", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSEO();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    try {
      await setDoc(doc(db, 'site_settings', 'seo'), seo);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save SEO", err);
      alert("Failed to save SEO settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-white">Loading SEO settings...</div>;

  return (
    <div className="glass-card p-8 rounded-3xl border border-white/5 space-y-8">
      <div className="mb-6">
        <h3 className="text-2xl font-bold font-display">Global SEO Settings</h3>
        <p className="text-white/60 text-sm mt-1">Define global keywords, canonicals, and metadata for the entire website.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <label className="block text-sm font-bold text-white/80 mb-2">Target Keywords (Comma separated)</label>
          <textarea
            value={seo.targetKeywords}
            onChange={e => setSeo(prev => ({ ...prev, targetKeywords: e.target.value }))}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
            placeholder="e.g. krijim uebfaqesh Kosovë, website design Kosovo, web development Kosovo, web design Pejë"
            rows={3}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-bold text-white/80 mb-2">Default Website Title</label>
            <input
              type="text"
              value={seo.defaultTitle}
              onChange={e => setSeo(prev => ({ ...prev, defaultTitle: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-white/80 mb-2">Default Description</label>
            <input
              type="text"
              value={seo.defaultDescription}
              onChange={e => setSeo(prev => ({ ...prev, defaultDescription: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-white/80 mb-2">Base Canonical URL</label>
            <input
              type="text"
              value={seo.canonicalUrl}
              onChange={e => setSeo(prev => ({ ...prev, canonicalUrl: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-white/80 mb-2">Robots Meta Rule</label>
            <input
              type="text"
              value={seo.robotsRule}
              onChange={e => setSeo(prev => ({ ...prev, robotsRule: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
              placeholder="index, follow"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-white/80 mb-2">Default Open Graph Image URL</label>
            <input
              type="text"
              value={seo.ogImage}
              onChange={e => setSeo(prev => ({ ...prev, ogImage: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary py-3 px-6 rounded-xl font-bold flex items-center gap-2"
          >
            {saving ? 'Duke ruajtur...' : success ? <><CheckCircle size={18}/> Ruajtur</> : <><Save size={18}/> Ruaj SEO Settings</>}
          </button>
        </div>
      </form>
    </div>
  );
};
